import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Rating,
  Divider,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  ThreeSixty as ThreeSixtyIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { tileCategories } from "./tilesData";
import { TileCategory } from "./tilesTypes";
import { getCategoryGradient, handleCall, handleWhatsApp, handleShare } from "../utils";
import TileViewer360 from "./TileViewer360";

const TilesPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTile, setSelectedTile] = useState<TileCategory | null>(null);
  const [view360, setView360] = useState<TileCategory | null>(null);

  const handleTileClick = (tile: TileCategory) => {
    setSelectedTile(tile);
  };

  const handle360View = (tile: TileCategory) => {
    setView360(tile);
  };

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case "In Stock":
        return "success";
      case "Limited Stock":
        return "warning";
      case "Out of Stock":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <Container maxWidth="lg">
        {/* Back Button */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/gallery')}
            sx={{
              color: "#1a237e",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              px: 3,
              py: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
            }}
          >
            Back to Gallery
          </Button>
        </Box>

        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: "bold",
              color: "#1a237e",
              mb: 3,
              fontSize: { xs: "2rem", md: "3.5rem" },
              background: "linear-gradient(45deg, #2196f3, #1976d2)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Tiles Collection
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "#666",
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            Discover Our Complete Range of Premium Tiles
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#888",
              maxWidth: "700px",
              mx: "auto",
              lineHeight: 1.6,
              mb: 4,
            }}
          >
            From floor to wall, indoor to outdoor - explore our comprehensive
            collection of high-quality tiles designed for every application and
            style preference.
          </Typography>
          
          {/* Quick Stats */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
            <Paper sx={{ p: 2, textAlign: "center", minWidth: "120px" }}>
              <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
                6
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Main Categories
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: "center", minWidth: "120px" }}>
              <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
                50+
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Tile Varieties
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: "center", minWidth: "120px" }}>
              <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
                360°
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                View Available
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* Main Categories Grid */}
        <Typography
          variant="h4"
          sx={{
            color: "#1a237e",
            fontWeight: "bold",
            mb: 4,
            textAlign: "center",
          }}
        >
          Tile Categories by Use
        </Typography>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, 
          gap: 4 
        }}>
          {tileCategories.map((tile, index) => (
            <Box key={tile.id}>
              <Card
                sx={{
                  height: "100%",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "16px",
                  border: "1px solid rgba(0,0,0,0.08)",
                  "&:hover": {
                    transform: "translateY(-12px) scale(1.02)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                  },
                }}
                onClick={() => handleTileClick(tile)}
              >
                {/* Badges */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    zIndex: 3,
                    display: "flex",
                    gap: 1,
                    flexDirection: "column",
                  }}
                >
                  {tile.isNew && (
                    <Chip
                      label="NEW"
                      size="small"
                      sx={{
                        backgroundColor: "#4caf50",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                  {tile.isPopular && (
                    <Chip
                      label="POPULAR"
                      size="small"
                      sx={{
                        backgroundColor: "#ff5722",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                </Box>

                {/* 360 View Button */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handle360View(tile);
                  }}
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    zIndex: 3,
                    backgroundColor: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,1)",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <ThreeSixtyIcon sx={{ color: "#1a237e" }} />
                </IconButton>

                <CardMedia
                  component="div"
                  sx={{
                    height: 240,
                    position: "relative",
                    background: getCategoryGradient("Tiles"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "1.4rem",
                    fontWeight: "bold",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "rgba(0,0,0,0.3)",
                      zIndex: 1,
                    },
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 2, textAlign: "center" }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                      {tile.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {tile.primaryUse.length} Applications
                    </Typography>
                  </Box>
                </CardMedia>

                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: "bold",
                        color: "#1a237e",
                        flex: 1,
                      }}
                    >
                      {tile.name}
                    </Typography>
                    <Chip
                      label={tile.availability}
                      size="small"
                      color={getAvailabilityColor(tile.availability) as any}
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Rating value={tile.rating} readOnly precision={0.1} size="small" />
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      ({tile.reviews} reviews)
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      mb: 2,
                      lineHeight: 1.6,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {tile.description}
                  </Typography>

                  {/* Key Features */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: "#1a237e", fontWeight: "bold", mb: 1 }}>
                      Key Features:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {tile.keyCharacteristics.slice(0, 2).map((feature, featureIndex) => (
                        <Typography
                          key={featureIndex}
                          variant="caption"
                          sx={{
                            color: "#1a237e",
                            backgroundColor: "#e8eaf6",
                            px: 1,
                            py: 0.5,
                            borderRadius: "12px",
                            fontSize: "0.7rem",
                            fontWeight: "500",
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      sx={{
                        backgroundColor: "#1a237e",
                        borderRadius: "8px",
                        "&:hover": { backgroundColor: "#3949ab" },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTileClick(tile);
                      }}
                    >
                      <InfoIcon sx={{ mr: 1, fontSize: "1rem" }} /> View Details
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: "#ffca28",
                        color: "#ffca28",
                        borderRadius: "8px",
                        minWidth: "auto",
                        px: 2,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsApp(tile.name);
                      }}
                    >
                      <WhatsAppIcon fontSize="small" />
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Detailed Tile Modal */}
        <Dialog
          open={!!selectedTile}
          onClose={() => setSelectedTile(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              maxHeight: "90vh",
            },
          }}
        >
          {selectedTile && (
            <>
              <DialogTitle
                sx={{
                  position: "relative",
                  background: getCategoryGradient("Tiles"),
                  color: "white",
                  pb: 2,
                }}
              >
                <Box sx={{ position: "relative", zIndex: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h5" component="h2" fontWeight="bold">
                      {selectedTile.name}
                    </Typography>
                    <IconButton onClick={() => setSelectedTile(null)} sx={{ color: "white" }}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                    <Rating value={selectedTile.rating} readOnly precision={0.1} size="small" />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      ({selectedTile.reviews} reviews)
                    </Typography>
                    <Chip
                      label={selectedTile.availability}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0,0,0,0.3)",
                    zIndex: 1,
                  }}
                />
              </DialogTitle>

              <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ p: 3 }}>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                    gap: 3 
                  }}>
                    <Box>
                      <Box
                        sx={{
                          height: 300,
                          background: getCategoryGradient("Tiles"),
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          position: "relative",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h4" fontWeight="bold">
                          {selectedTile.name}
                        </Typography>
                      </Box>

                      <Typography variant="h6" sx={{ color: "#1a237e", fontWeight: "bold", mb: 1 }}>
                        Key Characteristics
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                        {selectedTile.keyCharacteristics.map((characteristic, index) => (
                          <Chip
                            key={index}
                            label={characteristic}
                            size="small"
                            sx={{
                              backgroundColor: "#e8eaf6",
                              color: "#1a237e",
                              fontWeight: "500",
                            }}
                          />
                        ))}
                      </Box>

                      <Typography variant="h6" sx={{ color: "#1a237e", fontWeight: "bold", mb: 1 }}>
                        Common Materials
                      </Typography>
                      <List dense>
                        {selectedTile.commonMaterials.map((material, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                            <ListItemText
                              primary={`• ${material}`}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { color: "#666" },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>

                    <Box>
                      <Typography variant="h6" sx={{ color: "#1a237e", fontWeight: "bold", mb: 2 }}>
                        Description
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        {selectedTile.description}
                      </Typography>

                      <Typography variant="h6" sx={{ color: "#1a237e", fontWeight: "bold", mb: 1 }}>
                        Primary Applications
                      </Typography>
                      <List dense>
                        {selectedTile.primaryUse.map((use, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                            <ListItemText
                              primary={`• ${use}`}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { color: "#666" },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="h6" sx={{ color: "#1a237e", fontWeight: "bold", mb: 1 }}>
                        Specifications
                      </Typography>
                      <List dense>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={`Thickness: ${selectedTile.specifications.thickness}`}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={`Sizes: ${selectedTile.specifications.sizes.join(", ")}`}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={`Finish: ${selectedTile.specifications.finish.join(", ")}`}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                        {selectedTile.peiRating && (
                          <ListItem sx={{ px: 0, py: 0.5 }}>
                            <ListItemText
                              primary={`PEI Rating: ${selectedTile.peiRating}`}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        )}
                        {selectedTile.slipResistance && (
                          <ListItem sx={{ px: 0, py: 0.5 }}>
                            <ListItemText
                              primary={`Slip Resistance: ${selectedTile.slipResistance}`}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  </Box>

                  {/* Use Cases Section */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ color: "#1a237e", fontWeight: "bold", mb: 2 }}>
                      Recommended Use Cases
                    </Typography>
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                      gap: 2 
                    }}>
                      {selectedTile.useCases.map((useCase, index) => (
                        <Box key={index}>
                          <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                              {useCase.location}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                              <strong>Recommendation:</strong> {useCase.recommendation}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                              <strong>Key Checks:</strong> {useCase.keyChecks.join(", ")}
                            </Typography>
                          </Paper>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions sx={{ p: 3, gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleShare({ title: selectedTile.name, description: selectedTile.description })}
                  startIcon={<ShareIcon />}
                  sx={{ borderColor: "#1a237e", color: "#1a237e" }}
                >
                  Share
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handle360View(selectedTile)}
                  startIcon={<ThreeSixtyIcon />}
                  sx={{
                    backgroundColor: "#ff9800",
                    "&:hover": { backgroundColor: "#f57c00" },
                  }}
                >
                  360° View
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleWhatsApp(selectedTile.name)}
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    backgroundColor: "#25d366",
                    "&:hover": { backgroundColor: "#128c7e" },
                  }}
                >
                  WhatsApp Inquiry
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCall}
                  startIcon={<PhoneIcon />}
                  sx={{
                    backgroundColor: "#1a237e",
                    "&:hover": { backgroundColor: "#3949ab" },
                  }}
                >
                  Call Now
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* 360 View Modal */}
        <Dialog
          open={!!view360}
          onClose={() => setView360(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              backgroundColor: "#000",
              height: "90vh",
            },
          }}
        >
          {view360 && (
            <>
              <DialogTitle sx={{ color: "white", textAlign: "center", position: "relative", p: 2 }}>
                <Typography variant="h5" component="h2" fontWeight="bold">
                  {view360.name} - 360° Interactive View
                </Typography>
                <IconButton
                  onClick={() => setView360(null)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.2)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0, backgroundColor: "#000", height: "100%" }}>
                <TileViewer360
                  tileName={view360.name}
                  tileImages={view360.images360}
                  onClose={() => setView360(null)}
                />
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>

      {/* Add rotation animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default TilesPage;
